import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  IconButton,
  Drawer,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  Collapse,
  Typography,
  Box,
  Tooltip,
  Badge,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import { INFRA_NAME } from '../../../shared/constants/lookupMaps';

export type FilterCondition = {
  id: string;
  field: string;
  operator: 'equals' | 'notEquals' | 'contains' | 'range' | 'greaterThan' | 'lessThan' | 'in';
  value: any;
  value2?: any; // For range operator
};

export type FilterConfig = {
  field: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'boolean';
  options?: Array<{ value: any; label: string }>; // For select type
  isPrimary?: boolean; // Flag for primary/KPI filter
};

interface AdvancedFilterProps {
  filterConfigs: FilterConfig[];
  onFilterChange: (filters: FilterCondition[]) => void;
  initialFilters?: FilterCondition[];
  open: boolean;
  onClose: () => void;
  selectedKpi?: string; // The currently selected KPI field name
  filtersEnabled?: boolean; // External control for filter enable/disable
  onFiltersEnabledChange?: (enabled: boolean) => void; // Callback when toggle changes
}

export default function AdvancedFilter({
  filterConfigs,
  onFilterChange,
  initialFilters = [],
  open,
  onClose,
  selectedKpi,
  filtersEnabled = true,
  onFiltersEnabledChange,
}: AdvancedFilterProps) {
  const [tempFilters, setTempFilters] = useState<Record<string, any>>({});
  const [showOtherFilters, setShowOtherFilters] = useState(true);
  const [lastConfigKey, setLastConfigKey] = useState('');

  // Track which fields have values set
  const activeFieldCount = useMemo(() => {
    return Object.entries(tempFilters).filter(([_, value]) => {
      if (value === undefined || value === null || value === '') return false;
      if (typeof value === 'object' && !Array.isArray(value)) {
        return (value.min !== undefined && value.min !== '') || 
               (value.max !== undefined && value.max !== '');
      }
      return true;
    }).length;
  }, [tempFilters]);

  // Create a unique key for the current filter configs to detect changes
  const configKey = useMemo(() => {
    return filterConfigs.map(c => c.field).sort().join('|');
  }, [filterConfigs]);

  // Reset tempFilters when filterConfigs change (new KPI selected)
  useEffect(() => {
    if (configKey !== lastConfigKey && lastConfigKey !== '') {
      setTempFilters({});
    }
    setLastConfigKey(configKey);
  }, [configKey, lastConfigKey]);

  // Initialize tempFilters from initialFilters only when modal opens
  useEffect(() => {
    if (open && initialFilters.length > 0) {
      const filtersObj: Record<string, any> = {};
      initialFilters.forEach(filter => {
        if (filter.operator === 'range') {
          filtersObj[filter.field] = { min: filter.value, max: filter.value2 };
        } else {
          filtersObj[filter.field] = filter.value;
        }
      });
      setTempFilters(filtersObj);
    }
  }, [open, initialFilters]);

  // Organize filter configs - primary KPI filter on top
  const { primaryFilters, otherFilters } = useMemo(() => {
    const primary: FilterConfig[] = [];
    const other: FilterConfig[] = [];

    filterConfigs.forEach(config => {
      // Check if this field matches the selected KPI
      const fieldLower = config.field.toLowerCase().replace(/[^a-z0-9]/g, '');
      const kpiLower = (selectedKpi || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      
      const isPrimaryMatch = selectedKpi && (
        fieldLower === kpiLower ||
        fieldLower.includes(kpiLower) ||
        kpiLower.includes(fieldLower) ||
        config.label.toLowerCase().replace(/[^a-z0-9]/g, '').includes(kpiLower)
      );

      if (isPrimaryMatch) {
        primary.push({ ...config, isPrimary: true });
      } else {
        other.push(config);
      }
    });

    return { primaryFilters: primary, otherFilters: other };
  }, [filterConfigs, selectedKpi]);

  const handleFilterChange = useCallback((field: string, value: any) => {
    setTempFilters(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleApply = useCallback(() => {
    // Convert tempFilters object to FilterCondition array
    const conditions: FilterCondition[] = [];
    
    filterConfigs.forEach(config => {
      const value = tempFilters[config.field];
      
      // Skip if no value
      if (value === undefined || value === null || value === '') {
        return;
      }
      
      // Handle range filter for number types
      if (config.type === 'number' && typeof value === 'object') {
        const hasMin = value.min !== undefined && value.min !== null && value.min !== '';
        const hasMax = value.max !== undefined && value.max !== null && value.max !== '';
        if (hasMin || hasMax) {
          conditions.push({
            id: `filter-${config.field}`,
            field: config.field,
            operator: 'range',
            value: hasMin ? value.min : '',
            value2: hasMax ? value.max : ''
          });
        }
      } else {
        // Regular filter
        conditions.push({
          id: `filter-${config.field}`,
          field: config.field,
          operator: 'equals',
          value: value
        });
        
        // Special handling: If filtering by 'name' (infrastructure name), also add quantity > 0 filter
        const fieldLower = config.field.toLowerCase();
        if (fieldLower === 'name' || fieldLower === 'infraname') {
          // Add companion filter for quantity > 0
          conditions.push({
            id: `filter-quantity-auto`,
            field: 'quantity',
            operator: 'greaterThan',
            value: 0
          });
        }
      }
    });
    
    onFilterChange(conditions);
    onClose();
  }, [tempFilters, filterConfigs, onFilterChange, onClose]);

  const handleReset = useCallback(() => {
    setTempFilters({});
    onFilterChange([]);
  }, [onFilterChange]);

  const handleClearField = useCallback((field: string) => {
    setTempFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[field];
      return newFilters;
    });
  }, []);

  const renderFilterInput = useCallback((config: FilterConfig) => {
    const value = tempFilters[config.field];
    const hasValue = value !== undefined && value !== null && value !== '' && 
      !(typeof value === 'object' && value.min === '' && value.max === '');

    // Special handling for 'name' field (infrastructure names)
    const fieldLower = config.field.toLowerCase();
    const isInfraNameField = fieldLower === 'name' || fieldLower === 'infraname';
    
    if (isInfraNameField) {
      // Convert INFRA_NAME mapping to options array
      const infraOptions = Object.entries(INFRA_NAME).map(([key, label]) => ({
        value: key,
        label: label
      }));
      
      return (
        <div className="flex gap-2 items-center">
          <FormControl size="small" fullWidth>
            <InputLabel>{config.label}</InputLabel>
            <Select
              value={value || ''}
              onChange={(e) => handleFilterChange(config.field, e.target.value)}
              label={config.label}
              sx={{
                backgroundColor: value ? '#f3e5f5' : 'white',
              }}
            >
              <MenuItem value="">
                <em>Select {config.label}</em>
              </MenuItem>
              {infraOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {hasValue && (
            <IconButton
              size="small"
              onClick={() => handleClearField(config.field)}
              sx={{ color: '#6A1786' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </div>
      );
    }

    // For number type with range support
    if (config.type === 'number') {
      const rangeValue = typeof value === 'object' ? value : { min: '', max: '' };
      return (
        <div className="flex gap-2 items-center">
          <TextField
            size="small"
            type="number"
            placeholder="Min"
            value={rangeValue.min || ''}
            onChange={(e) => handleFilterChange(config.field, { ...rangeValue, min: e.target.value })}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: rangeValue.min ? '#f3e5f5' : 'white',
              }
            }}
          />
          <span className="text-gray-500 font-medium">to</span>
          <TextField
            size="small"
            type="number"
            placeholder="Max"
            value={rangeValue.max || ''}
            onChange={(e) => handleFilterChange(config.field, { ...rangeValue, max: e.target.value })}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: rangeValue.max ? '#f3e5f5' : 'white',
              }
            }}
          />
          {hasValue && (
            <IconButton
              size="small"
              onClick={() => handleClearField(config.field)}
              sx={{ color: '#6A1786' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </div>
      );
    }

    // For select type with options
    if (config.type === 'select' && config.options) {
      return (
        <div className="flex gap-2 items-center">
          <FormControl size="small" fullWidth>
            <InputLabel>{config.label}</InputLabel>
            <Select
              value={value || ''}
              onChange={(e) => handleFilterChange(config.field, e.target.value)}
              label={config.label}
              sx={{
                backgroundColor: value ? '#f3e5f5' : 'white',
              }}
            >
              <MenuItem value="">
                <em>Select {config.label}</em>
              </MenuItem>
              {config.options.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {hasValue && (
            <IconButton
              size="small"
              onClick={() => handleClearField(config.field)}
              sx={{ color: '#6A1786' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </div>
      );
    }

    // For boolean type
    if (config.type === 'boolean') {
      return (
        <div className="flex gap-2 items-center">
          <FormControl size="small" fullWidth>
            <InputLabel>{config.label}</InputLabel>
            <Select
              value={value || ''}
              onChange={(e) => handleFilterChange(config.field, e.target.value)}
              label={config.label}
              sx={{
                backgroundColor: value ? '#f3e5f5' : 'white',
              }}
            >
              <MenuItem value="">
                <em>Select {config.label}</em>
              </MenuItem>
              <MenuItem value="1">Yes</MenuItem>
              <MenuItem value="0">No</MenuItem>
            </Select>
          </FormControl>
          {hasValue && (
            <IconButton
              size="small"
              onClick={() => handleClearField(config.field)}
              sx={{ color: '#6A1786' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </div>
      );
    }

    // Default: text input
    return (
      <div className="flex gap-2 items-center">
        <TextField
          size="small"
          label={config.label}
          placeholder={`Enter ${config.label}`}
          value={value || ''}
          onChange={(e) => handleFilterChange(config.field, e.target.value)}
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: value ? '#f3e5f5' : 'white',
            }
          }}
        />
        {hasValue && (
          <IconButton
            size="small"
            onClick={() => handleClearField(config.field)}
            sx={{ color: '#6A1786' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </div>
    );
  }, [tempFilters, handleFilterChange, handleClearField]);

  const renderFilterSection = (configs: FilterConfig[], title: string, isPrimary: boolean) => {
    if (configs.length === 0) return null;

    return (
      <div className={`${isPrimary ? 'bg-purple-50 border border-purple-200' : 'bg-white'} rounded-lg p-4`}>
        <div className="flex items-center gap-2 mb-4">
          {isPrimary && <StarIcon sx={{ color: '#6A1786', fontSize: 20 }} />}
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 600, 
              color: isPrimary ? '#6A1786' : '#374151',
              fontSize: isPrimary ? '16px' : '14px'
            }}
          >
            {title}
          </Typography>
          {isPrimary && (
            <Chip 
              label="Primary Filter" 
              size="small" 
              sx={{ 
                backgroundColor: '#6A1786', 
                color: 'white',
                fontSize: '11px',
                height: '22px'
              }} 
            />
          )}
        </div>
        <div className="space-y-4">
          {configs.map((config) => (
            <div key={config.field}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {config.label}
                {tempFilters[config.field] && (
                  <CheckCircleIcon 
                    sx={{ 
                      fontSize: 16, 
                      color: '#22c55e', 
                      ml: 1, 
                      verticalAlign: 'middle' 
                    }} 
                  />
                )}
              </label>
              {renderFilterInput(config)}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 500, md: 600 },
          maxWidth: '100%'
        }
      }}
    >
      <div className="h-full flex flex-col bg-gray-50">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white shadow-sm">
          <div className="flex items-center gap-3">
            <Badge badgeContent={activeFieldCount} color="secondary">
              <FilterAltIcon sx={{ color: '#6A1786', fontSize: 28 }} />
            </Badge>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Filter Options</h2>
              <p className="text-xs text-gray-500">
                {activeFieldCount > 0 
                  ? `${activeFieldCount} filter${activeFieldCount > 1 ? 's' : ''} configured` 
                  : 'Configure filters to narrow results'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onFiltersEnabledChange && (
              <Tooltip title={filtersEnabled ? 'Disable all filters' : 'Enable all filters'}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={filtersEnabled}
                      onChange={(e) => onFiltersEnabledChange(e.target.checked)}
                      color="secondary"
                      size="small"
                    />
                  }
                  label={
                    <span className="text-xs text-gray-600">
                      {filtersEnabled ? 'Active' : 'Paused'}
                    </span>
                  }
                  sx={{ mr: 1 }}
                />
              </Tooltip>
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </div>
        </div>

        {/* Filter Enabled/Disabled Banner */}
        {!filtersEnabled && activeFieldCount > 0 && (
          <div className="px-4 py-2 bg-amber-50 border-b border-amber-200">
            <p className="text-sm text-amber-700 flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
              Filters are paused. Toggle switch to apply filters.
            </p>
          </div>
        )}

        {/* Filter Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {filterConfigs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FilterAltIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 2 }} />
              <p className="font-medium">No filters available</p>
              <p className="text-sm mt-2">Please select a category and KPI to see filter options</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Primary KPI Filter Section */}
              {primaryFilters.length > 0 && (
                renderFilterSection(
                  primaryFilters, 
                  `${selectedKpi ? selectedKpi.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim() : 'Selected KPI'} Filter`,
                  true
                )
              )}

              {/* Other Filters Section */}
              {otherFilters.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200">
                  <button
                    onClick={() => setShowOtherFilters(!showOtherFilters)}
                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Typography variant="subtitle1" sx={{ fontWeight: 500, color: '#374151' }}>
                        Other Filters
                      </Typography>
                      <Chip 
                        label={`${otherFilters.length} fields`} 
                        size="small" 
                        sx={{ fontSize: '11px', height: '22px' }} 
                      />
                    </div>
                    {showOtherFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </button>
                  <Collapse in={showOtherFilters}>
                    <Divider />
                    <div className="p-4 space-y-4">
                      {otherFilters.map((config) => (
                        <div key={config.field}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {config.label}
                            {tempFilters[config.field] && (
                              <CheckCircleIcon 
                                sx={{ 
                                  fontSize: 16, 
                                  color: '#22c55e', 
                                  ml: 1, 
                                  verticalAlign: 'middle' 
                                }} 
                              />
                            )}
                          </label>
                          {renderFilterInput(config)}
                        </div>
                      ))}
                    </div>
                  </Collapse>
                </div>
              )}

              {/* Active Filters Summary */}
              {activeFieldCount > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Typography variant="subtitle2" sx={{ color: '#15803d', fontWeight: 600 }}>
                      Active Filters Summary
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<ClearAllIcon />}
                      onClick={() => setTempFilters({})}
                      sx={{ 
                        textTransform: 'none', 
                        fontSize: '12px',
                        color: '#dc2626'
                      }}
                    >
                      Clear All
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(tempFilters).map(([field, value]) => {
                      if (value === undefined || value === null || value === '') return null;
                      const config = filterConfigs.find(c => c.field === field);
                      if (!config) return null;

                      let displayValue = '';
                      if (typeof value === 'object' && !Array.isArray(value)) {
                        const parts = [];
                        if (value.min) parts.push(`Min: ${value.min}`);
                        if (value.max) parts.push(`Max: ${value.max}`);
                        displayValue = parts.join(', ');
                      } else {
                        displayValue = String(value);
                      }

                      if (!displayValue) return null;

                      return (
                        <Chip
                          key={field}
                          label={`${config.label}: ${displayValue}`}
                          size="small"
                          onDelete={() => handleClearField(field)}
                          sx={{
                            backgroundColor: '#dcfce7',
                            color: '#15803d',
                            '& .MuiChip-deleteIcon': {
                              color: '#16a34a',
                              '&:hover': { color: '#dc2626' }
                            }
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer with Actions */}
        <div className="border-t border-gray-200 p-4 bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
          <div className="flex gap-3">
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              onClick={handleReset}
              startIcon={<ClearAllIcon />}
              sx={{ 
                textTransform: 'none',
                borderRadius: '8px',
                padding: '10px 24px'
              }}
            >
              Reset All
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleApply}
              disabled={!filtersEnabled}
              startIcon={<CheckCircleIcon />}
              sx={{ 
                textTransform: 'none',
                backgroundColor: '#6A1786',
                borderRadius: '8px',
                padding: '10px 24px',
                '&:hover': {
                  backgroundColor: '#551365'
                },
                '&.Mui-disabled': {
                  backgroundColor: '#d1d5db',
                  color: '#6b7280'
                }
              }}
            >
              Apply Filters {activeFieldCount > 0 && `(${activeFieldCount})`}
            </Button>
          </div>
        </div>
      </div>
    </Drawer>
  );
}
